import './main.css';
import { createApp, type App as VueApp } from 'vue';
import { request, lotsUIEventInstance, setSidebarApplicationCode } from '@annto/ui';
import 'mdesign3/dist/index.css';
import App from './App.vue';
import router from './router';
import store from './store';
import './iconfont/iconfont.css';
import config from './config/user.env';
import { checkLogin, logout } from './utils/index';
import { bootstrapLoggedInRolePrefetch } from '@/utils/roleResourcesCache';
import { bootstrapHomeMicroAppPrefetch } from '@/utils/prefetchMicroAppsByAuthority';
import { registerPageTrackGuard } from '@/utils/bigLogData/trackHelper';
import microApp from '@micro-zoe/micro-app';
import microPluginMap from '@micro-zoe/micro-plugin-map';
import NProgress from 'nprogress';
import 'nprogress/nprogress.css';
import '@/styles/fonts/iconfont.css';
import '@/styles/index.less';
import Cookies from 'js-cookie';
import globalMethod from '@/utils/globalMethod';
import { runAfterDomReady, runWhenIdle } from '@/utils/deferNonCritical';

setSidebarApplicationCode(config.applicationCode);

/** 美信内嵌：DOM 就绪后再动态加载 cordova / mxNative，桌面 Web 不请求 */
const bootstrapNativeBridge = () => {
  if (!/mideaconnect/i.test(navigator.userAgent || '')) {
    return;
  }
  runAfterDomReady(() => {
    Promise.all([
      import('../public/cordova/cordova.js'),
      import('@midea/mx-native'),
      import('../mx.native.config.js'),
    ])
      .then(([cordovaMod, mxNativeMod, mxConfigMod]) => {
        const cordovaObj = (cordovaMod as any).default || cordovaMod;
        cordovaObj.init?.();
        const MxNative = (mxNativeMod as any).default || mxNativeMod;
        const mxNativeConfig = (mxConfigMod as any).default || mxConfigMod;
        new MxNative(mxNativeConfig);
      })
      .catch((err) => {
        console.warn('[native-bridge] load failed', err);
      });
  });
};
bootstrapNativeBridge();

// lots ui 组件中的库使用到这个请求的时候
// 请求拦截器
request.interceptors.request.use(
  (configs) => {
    NProgress.start();
    return configs;
  },
  (error) => {
    // 修正 NProgress 的调用逻辑，避免NProgress.start() 和 NProgress.done() 的调用次数不一致，导致计数器不平衡，导致进度条卡住的问题。
    // 在请求拦截器的错误处理中，你调用了 NProgress.done()，但此时请求可能未发送（如配置错误），导致 start() 未被调用，却提前调用了 done()，破坏了计数器平衡。
    // NProgress.done();
    return Promise.reject(error);
  },
);
// 响应拦截器
request.interceptors.response.use(
  (response) => {
    NProgress.done();
    return response;
  },
  (error) => {
    NProgress.done();
    return Promise.reject(error);
  },
);
request.defaults.baseURL = config.apiUrl;

microApp.start({
  'disable-memory-router': false, // 关闭虚拟路由系统
  'disable-patch-request': true, // 关闭对子应用请求的拦截
  prefetchDelay: 1000,
  iframeSrc: window.location.origin + window.location.pathname + 'empty.html',
  // 不在此静态预加载子应用：避免与登录首屏抢带宽。
  // 实际策略：登录页预首页 1 个；进首页后 3500ms 预其余（见 prefetchMicroAppsByAuthority）。
  // 首页强刷新不预首页子应用（与挂载间隔过短，预加载无作用）。
  preFetchApps: [],
  excludeAssetFilter: (assetUrl) => {
    const whiteList: any[] = [];
    const whiteWorks = ['baidu', 'map', 'mui.midea.com', 'iop.midea.com']; //'mui.midea.com' 解决人员选择器组件资源加载异常导致图标无法正常显示问题
    if (whiteList.includes(assetUrl)) {
      return true;
    }
    return whiteWorks.some((work) => assetUrl.includes(work));
  },

  // 全局资源配置
  globalAssets: {
    js: [],
    css: [],
  },

  plugins: {
    global: [microPluginMap],
  },
});
// 角色资源接口预取（与 initSystem 并行）。
// 已登录强刷新：只标记当前子应用路径，不预首页（与挂载间隔过短，无作用）。
bootstrapLoggedInRolePrefetch();
bootstrapHomeMicroAppPrefetch();
lotsUIEventInstance.listen('logout', (flag = false) => {
  logout(flag);
});
const _has = (value: string) => {
  let isExist = false;
  const btnPermissionsArr = store.getters.btnPermissions;
  if (btnPermissionsArr === undefined || btnPermissionsArr === null) {
    return false;
  }
  if (btnPermissionsArr.indexOf(value) > -1) {
    isExist = true;
  } else {
    isExist = false;
  }
  return isExist;
};

/** 壳内表格：DOM 就绪后动态注册，避免 vxe 进首屏同步包 */
const registerVxeTable = (app: VueApp) => {
  runAfterDomReady(() => {
    import('@annto/load-vxe')
      .then(({ loadVxeTable }) => {
        app.use(loadVxeTable);
      })
      .catch((err) => {
        console.warn('[vxe] load failed', err);
      });
  });
};

/** 监控：DOM 就绪 + idle 后再动态 import */
const initAiMito = (app: VueApp) => {
  runWhenIdle(() => {
    Promise.all([import('@aimitojs/browser'), import('@aimitojs/vue')])
      .then(([browserMod, vueMod]) => {
        const init = browserMod.init;
        const vuePlugin = vueMod.vuePlugin;
        init(
          {
            vue: app,
            appKey: config.monitor_app_key,
            serviceName: 'c-el-pc',
            dsn: 'https://mwmr.midea.com/xops/web-endpoint/report',
            backUserId() {
              return Cookies.get('apmUser') || '';
            },
          },
          [vuePlugin],
        );
      })
      .catch((err) => {
        console.warn('[aimito] init failed', err);
      });
  });
};

(async function () {
  const whiteList = ['/login', '/preview', '/selfPrint', '/tms/selfPrint', '/todo-task-h5view', '/h5/bulletin-board']; //otp子系统白名单: '/preview', '/selfPrint', '/tms/selfPrint', '/todo-task-h5view'
  const loginRequiredList = ['/h5/overdueUnfulfilled24h'];
  await checkLogin(router, whiteList, loginRequiredList);
  registerPageTrackGuard(router);
  const app = createApp(App)
    .use(store)
    .use(router)
    .use(globalMethod)
    .directive('has', {
      mounted: (el, binding) => {
        const value = binding.value.toString();
        const auths = _has(value);
        if (value && !auths) {
          if (el.parentNode !== null) {
            el.parentNode.removeChild(el);
          }
        }
      },
    });

  app.mount('#app');
  // 首屏先挂载；非关键第三方统一 DOMContentLoaded / idle 后再动态 import
  registerVxeTable(app);
  initAiMito(app);
})();
